import {useMutation, useQueryClient} from '@tanstack/react-query';

import {teamKeys} from '@/features/team/hooks';
import {trustAccountKeys} from '@/features/trust-accounts/hooks';
import {useApiClient} from '@/hooks';
import {useUserDetails} from '@/lib/auth';

import {accountSettingsApi} from '../api';
import {accountSettingsKeys, persistUserProfile} from './useUserProfileQuery';

import type {UpdateUserProfilePayload} from '@sollapay/types';

export function useUpdateUserProfileMutation() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const {user} = useUserDetails();

  return useMutation({
    mutationFn: (payload: UpdateUserProfilePayload) =>
      accountSettingsApi.updateProfile(client, payload),
    onSuccess: response => {
      // The PATCH response already carries the fresh profile — no need to invalidate and
      // trigger a redundant GET /user/profile round trip for data we already have.
      queryClient.setQueryData(accountSettingsKeys.profile(), response);
      persistUserProfile(user?.sub, response.data);
      // The team list and trust-accounts list/detail (trustee column) show this same
      // identity (name/avatar) for the current user's own row — without this, a save
      // wouldn't show up until the next navigation or window-focus refetch. Documents are
      // included (uploader avatar) but balances/transactions are not — they carry no
      // identity fields, so refetching them here would be wasted work.
      void queryClient.invalidateQueries({queryKey: teamKeys.members()});
      void queryClient.invalidateQueries({queryKey: trustAccountKeys.lists()});
      void queryClient.invalidateQueries({queryKey: trustAccountKeys.details()});
      void queryClient.invalidateQueries({queryKey: [...trustAccountKeys.all, 'documents']});
    }
  });
}
